import type { IQuoteRequest, IQuoteResponse } from "../services/api";
import {
  buildQuoteRefreshRequest,
  type QuoteRefreshRoute,
} from "./buildQuoteRefreshRequest";

export const QUOTE_STALE_BUFFER_SECONDS = 5;

export type QuoteSnapshot = Pick<
  IQuoteResponse,
  | "signedQuote"
  | "expiresAt"
  | "sendAmount"
  | "receiveAmount"
  | "sourceCurrency"
  | "destinationCurrency"
  | "fundingMethod"
  | "payoutMethod"
  | "capabilityKey"
  | "anchor"
  | "cardId"
>;

export function isQuoteExpiredError(err: unknown): boolean {
  const axiosError = err as {
    response?: { data?: { error?: { code?: string } } };
  };
  return axiosError?.response?.data?.error?.code === "QUOTE_EXPIRED";
}

export function isQuoteStale(
  expiresAt: string | undefined,
  bufferSec = QUOTE_STALE_BUFFER_SECONDS,
  nowMs = Date.now(),
): boolean {
  if (!expiresAt) return true;
  const exp = new Date(expiresAt).getTime();
  if (Number.isNaN(exp)) return true;
  return exp <= nowMs + bufferSec * 1000;
}

/**
 * Resolves when zustand persist has rehydrated from localStorage.
 * SendingMoney must wait so a CIP remount does not create with an empty store.
 */
export function waitForPersistHydration(persistApi: {
  hasHydrated: () => boolean;
  onFinishHydration: (fn: () => void) => void;
}): Promise<void> {
  if (persistApi.hasHydrated()) return Promise.resolve();
  return new Promise((resolve) => {
    persistApi.onFinishHydration(() => resolve());
  });
}

export async function ensureFreshQuote(args: {
  quote: QuoteSnapshot;
  route: QuoteRefreshRoute;
  getQuote: (req: IQuoteRequest) => Promise<IQuoteResponse>;
}): Promise<IQuoteResponse> {
  if (!isQuoteStale(args.quote.expiresAt)) {
    return args.quote as IQuoteResponse;
  }
  return args.getQuote(buildQuoteRefreshRequest(args.quote, args.route));
}

/**
 * Recotiza si el JWT ya venció (o está en el buffer), crea, y si el backend
 * igual responde QUOTE_EXPIRED recotiza una vez más y reintenta create.
 */
export async function createWithFreshQuote<T>(args: {
  quote: QuoteSnapshot;
  route: QuoteRefreshRoute;
  getQuote: (req: IQuoteRequest) => Promise<IQuoteResponse>;
  create: (signedQuote: string) => Promise<T>;
}): Promise<{ result: T; quote: IQuoteResponse }> {
  let quote = await ensureFreshQuote(args);
  try {
    const result = await args.create(quote.signedQuote);
    return { result, quote };
  } catch (err) {
    if (!isQuoteExpiredError(err)) throw err;
    quote = await args.getQuote(
      buildQuoteRefreshRequest(quote, args.route),
    );
    const result = await args.create(quote.signedQuote);
    return { result, quote };
  }
}
